import { Toast as BaseToast } from "@base-ui/react/toast";
import alertIcon from "@loot-os/icons/icons/alert";
import closeIcon from "@loot-os/icons/icons/close";
import infoIcon from "@loot-os/icons/icons/info";
import successIcon from "@loot-os/icons/icons/success";
import type { ReactNode } from "react";

import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { Icon } from "./Icon.js";
import type { AppToasts, ToastData } from "./useToast.js";

const KINDS = {
  success: { icon: successIcon, color: "text-status-success" },
  error: { icon: alertIcon, color: "text-status-danger" },
  info: { icon: infoIcon, color: "text-accent-highlight" },
} as const;

export type ToastKind = keyof typeof KINDS;

export interface ToasterProps {
  children: ReactNode;
  /** Milliseconds before a toast leaves on its own. */
  timeout?: number;
  /** The close button's name. */
  closeLabel?: string;
  /** An app-owned manager (`createAppToasts`), for toasts shown from outside React. */
  manager?: AppToasts["manager"];
}

/** Mount once around the app; `useToast()` anywhere below it shows a toast. */
export function Toaster({
  children,
  timeout = 4000,
  closeLabel = "Dismiss",
  manager,
}: ToasterProps) {
  return (
    <BaseToast.Provider timeout={timeout} limit={2} toastManager={manager}>
      {children}
      <BaseToast.Portal>
        <BaseToast.Viewport className="fixed top-4 left-1/2 z-[60] flex w-[min(380px,calc(100vw-32px))] -translate-x-1/2 flex-col gap-2 outline-none">
          <ToastList closeLabel={closeLabel} />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  );
}

function ToastList({ closeLabel }: { closeLabel: string }) {
  const { toasts } = BaseToast.useToastManager();
  return toasts.map((toast) => {
    const kind = KINDS[(toast.type as ToastKind | undefined) ?? "info"] ?? KINDS.info;
    return (
      <BaseToast.Root
        key={toast.id}
        toast={toast}
        className="glass flex items-start gap-3 rounded-xl px-4 py-3.5 shadow-float transition-[opacity,translate] duration-base ease-enter data-ending-style:-translate-y-2 data-ending-style:opacity-0 data-starting-style:-translate-y-2 data-starting-style:opacity-0 motion-reduce:transition-none"
      >
        {(toast.data as ToastData | undefined)?.media ?? (
          <Icon icon={kind.icon} size={20} className={cn("shrink-0", kind.color)} />
        )}
        <BaseToast.Content className="flex min-w-0 flex-1 flex-col gap-0.5">
          <BaseToast.Title className="font-body text-body-b1-strong text-text-primary" />
          <BaseToast.Description className="font-body text-body-b2 text-text-secondary" />
        </BaseToast.Content>
        <BaseToast.Close
          aria-label={closeLabel}
          className={cn(
            "shrink-0 cursor-pointer rounded-xs text-text-tertiary hover:text-text-primary",
            FOCUS_RING,
          )}
        >
          <Icon icon={closeIcon} />
        </BaseToast.Close>
      </BaseToast.Root>
    );
  });
}
